import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { RootState } from "../redux/reducers";
import { MaterialService } from "../services/MaterialService";

const { Title } = Typography;
const { Option } = Select;

interface Material {
  id: number;
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
  createdDate: string;
  updatedDate?: string;
  createdBy?: string;
  updatedBy?: string;
}

const MaterialListScreen: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<Material[]>([]);

  const currencyOptions = [
    { value: "₺", label: "₺ (TL)" },
    { value: "$", label: "$ (USD)" },
    { value: "€", label: "€ (EUR)" },
  ];

  useEffect(() => {
    loadMaterials();
  }, [pagination.current, pagination.pageSize, searchTerm]);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const response = await MaterialService.getMaterials({
        searchTerm: searchTerm || undefined,
        page: pagination.current,
        pageSize: pagination.pageSize,
      });

      if (response.success) {
        const data = response.data as any;
        setMaterials(data.materials || []);
        setPagination((prev) => ({
          ...prev,
          total: data.totalCount || 0,
        }));
      } else {
        message.error(response.message || "Malzemeler yüklenirken hata oluştu");
      }
    } catch (error) {
      message.error("Malzemeler yüklenirken hata oluştu");
      console.error("Error loading materials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const showModal = (material?: Material) => {
    setEditingMaterial(material || null);
    setIsModalVisible(true);

    if (material) {
      form.setFieldsValue({
        stockCode: material.stockCode,
        materialName: material.materialName,
        materialSecondName: material.materialSecondName,
        price: material.price,
        currency: material.currency,
      });
    } else {
      form.resetFields();
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingMaterial) {
        // Update existing material
        const response = await MaterialService.updateMaterial({
          id: editingMaterial.id,
          ...values,
        });

        if (response.success) {
          message.success("Malzeme başarıyla güncellendi");
          loadMaterials();
          setIsModalVisible(false);
        } else {
          message.error(
            response.message || "Malzeme güncellenirken hata oluştu"
          );
        }
      } else {
        // Create new material
        const response = await MaterialService.createMaterial(values);

        if (response.success) {
          message.success("Malzeme başarıyla oluşturuldu");
          loadMaterials();
          setIsModalVisible(false);
        } else {
          message.error(
            response.message || "Malzeme oluşturulurken hata oluştu"
          );
        }
      }
    } catch (error) {
      console.error("Error saving material:", error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await MaterialService.deleteMaterial(id);

      if (response.success) {
        message.success("Malzeme başarıyla silindi");
        loadMaterials();
      } else {
        message.error(response.message || "Malzeme silinirken hata oluştu");
      }
    } catch (error) {
      message.error("Malzeme silinirken hata oluştu");
      console.error("Error deleting material:", error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMaterials.length === 0) {
      message.warning("Silinecek malzeme seçiniz");
      return;
    }

    try {
      const materialIds = selectedMaterials.map((material) => material.id);
      const response = await MaterialService.bulkDeleteMaterials(materialIds);

      if (response.success) {
        message.success(response.message || "Malzemeler başarıyla silindi");
        loadMaterials();
        setSelectedRowKeys([]);
        setSelectedMaterials([]);
      } else {
        message.error(
          response.message || "Toplu silme işlemi sırasında hata oluştu"
        );
        if (response.errors && response.errors.length > 0) {
          console.error("Bulk delete errors:", response.errors);
        }
      }
    } catch (error) {
      message.error("Toplu silme işlemi sırasında hata oluştu");
      console.error("Error in bulk delete:", error);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: Material[]) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedMaterials(selectedRows);
    },
  };

  const handleCSVImport = async (file: File) => {
    try {
      const data = await readCSVFile(file);
      console.log("CSV Import Data:", data);

      const response = await MaterialService.importCSV(data);

      if (response.success) {
        message.success(response.message || "CSV başarıyla içe aktarıldı");
        loadMaterials();
      } else {
        message.error(response.message || "CSV içe aktarılırken hata oluştu");
        if (response.errors && response.errors.length > 0) {
          console.error("Import errors:", response.errors);
        }
      }
    } catch (error) {
      message.error("CSV dosyası okunurken hata oluştu");
      console.error("Error reading CSV:", error);
    }
  };

  const readCSVFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;

          // Check if it's a CSV file and handle semicolon delimiters
          if (file.name.toLowerCase().endsWith(".csv")) {
            const text = data as string;
            console.log("Raw text data:", text.substring(0, 500)); // Show first 500 chars
            const lines = text.split("\n");
            console.log("Total lines:", lines.length);
            console.log("First line:", lines[0]);
            const headers = lines[0].split(";");
            console.log("Headers:", headers);

            const jsonData = lines
              .slice(1)
              .map((line, index) => {
                const values = line.split(";");
                const row: any = {};
                headers.forEach((header, index) => {
                  row[header.trim()] = values[index]
                    ? values[index].trim().replace(/"/g, "")
                    : "";
                });
                if (index < 3) {
                  // Log first 3 rows for debugging
                  console.log(`Row ${index + 1}:`, row);
                }
                return row;
              })
              .filter((row) =>
                Object.values(row).some((value) => value !== "")
              );

            console.log("Raw CSV Data:", jsonData);
            processCSVData(jsonData, resolve, reject);
          } else {
            // Handle Excel files
            const workbook = XLSX.read(data, {
              type: "binary",
              codepage: 65001,
            }); // UTF-8 encoding
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            console.log("Raw CSV Data:", jsonData);
            processCSVData(jsonData, resolve, reject);
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Dosya okunamadı"));
      reader.readAsText(file, "iso-8859-9");
    });
  };

  const processCSVData = (jsonData: any[], resolve: any, reject: any) => {
    try {
      // Map CSV columns to our model
      const mappedData = jsonData
        .filter((row: any) => {
          // Başlık satırını ve boş satırları filtrele
          const stockCode = row["Stok Kodu"] || "";
          const materialName = row["Stok Adı"] || "";
          console.log("Filter check:", { stockCode, materialName, row });
          return stockCode && materialName && stockCode !== "Stok Kodu";
        })
        .map((row: any) => {
          const priceValue = row["Fiyat"] || "0";
          const price = parseFloat(
            priceValue.toString().replaceAll(".", "").replace(",", ".")
          );
          const currency = row["Kur"] || "₺";

          console.log(
            "Mapping row:",
            row,
            "Price value:",
            priceValue,
            "Parsed price:",
            price,
            "Currency:",
            currency
          );

          return {
            stockCode: String(row["Stok Kodu"] || "").trim(),
            materialName: String(row["Stok Adı"] || "").trim(),
            materialSecondName: String(row["İkinci Adı"] || "").trim(),
            price: isNaN(price) ? 0 : price,
            currency: String(currency).trim(),
          };
        });

      console.log("Mapped Data:", mappedData);
      resolve(mappedData);
    } catch (error) {
      reject(error);
    }
  };

  const columns = [
    {
      title: "Stok Kodu",
      dataIndex: "stockCode",
      key: "stockCode",
      width: 120,
      sorter: (a: Material, b: Material) =>
        a.stockCode.localeCompare(b.stockCode),
    },
    {
      title: "Malzeme Adı",
      dataIndex: "materialName",
      key: "materialName",
      sorter: (a: Material, b: Material) =>
        a.materialName.localeCompare(b.materialName),
    },
    {
      title: "Malzeme İkinci Adı",
      dataIndex: "materialSecondName",
      key: "materialSecondName",
      render: (text: string) => text || "-",
    },
    {
      title: "Fiyat",
      dataIndex: "price",
      key: "price",
      width: 120,
      render: (price: number, record: Material) => (
        <span>
          {price.toLocaleString("tr-TR")} {record.currency}
        </span>
      ),
      sorter: (a: Material, b: Material) => a.price - b.price,
    },
    {
      title: "Para Birimi",
      dataIndex: "currency",
      key: "currency",
      width: 100,
      render: (currency: string) => <Tag color="blue">{currency}</Tag>,
    },
    {
      title: "Oluşturulma Tarihi",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString("tr-TR"),
      sorter: (a: Material, b: Material) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    },
    ...(user?.role === "admin"
      ? [
          {
            title: "İşlemler",
            key: "actions",
            width: 120,
            render: (text: any, record: Material) => (
              <Space size="small">
                <Tooltip title="Düzenle">
                  <Button
                    type="primary"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => showModal(record)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Bu malzemeyi silmek istediğinizden emin misiniz?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Evet"
                  cancelText="Hayır"
                >
                  <Tooltip title="Sil">
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Malzeme Listesi
            </Title>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder="Stok kodu, malzeme adı ile ara..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              {user?.role === "admin" && (
                <Upload
                  accept=".csv,.xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleCSVImport(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>CSV İçe Aktar</Button>
                </Upload>
              )}
              {user?.role === "admin" && selectedMaterials.length > 0 && (
                <Popconfirm
                  title={`${selectedMaterials.length} malzemeyi silmek istediğinizden emin misiniz?`}
                  onConfirm={handleBulkDelete}
                  okText="Evet"
                  cancelText="Hayır"
                >
                  <Button type="primary" danger icon={<DeleteOutlined />}>
                    Seçilenleri Sil ({selectedMaterials.length})
                  </Button>
                </Popconfirm>
              )}
              {user?.role === "admin" && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => showModal()}
                >
                  Yeni Malzeme
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={loadMaterials}
                loading={loading}
              >
                Yenile
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={materials}
          rowKey="id"
          loading={loading}
          rowSelection={user?.role === "admin" ? rowSelection : undefined}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} malzeme`,
            pageSizeOptions: ["25", "50", "100", "200"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingMaterial ? "Malzeme Düzenle" : "Yeni Malzeme"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            currency: "₺",
          }}
        >
          <Form.Item
            name="stockCode"
            label="Stok Kodu"
            rules={[
              { required: true, message: "Stok kodu gereklidir" },
              { max: 50, message: "Stok kodu en fazla 50 karakter olabilir" },
            ]}
          >
            <Input placeholder="Stok kodunu girin" />
          </Form.Item>

          <Form.Item
            name="materialName"
            label="Malzeme Adı"
            rules={[
              { required: true, message: "Malzeme adı gereklidir" },
              {
                max: 200,
                message: "Malzeme adı en fazla 200 karakter olabilir",
              },
            ]}
          >
            <Input placeholder="Malzeme adını girin" />
          </Form.Item>

          <Form.Item
            name="materialSecondName"
            label="Malzeme İkinci Adı"
            rules={[
              {
                max: 200,
                message: "Malzeme ikinci adı en fazla 200 karakter olabilir",
              },
            ]}
          >
            <Input placeholder="Malzeme ikinci adını girin (opsiyonel)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="price"
                label="Fiyat"
                rules={[
                  { required: true, message: "Fiyat gereklidir" },
                  {
                    type: "number",
                    min: 0,
                    message: "Fiyat 0'dan büyük olmalıdır",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Fiyatı girin"
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currency"
                label="Para Birimi"
                rules={[{ required: true, message: "Para birimi gereklidir" }]}
              >
                <Select placeholder="Para birimi seçin">
                  {currencyOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default connect()(MaterialListScreen);
